import * as Dialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SUPPORTED_CITIES, useCity } from "@/contexts/CityContext";

// El banner de cookies debe resolverse ANTES de abrir el chooser: el overlay
// del modal bloquearía sus clics y el visitante quedaría atrapado. Mismo
// contrato que PostHogProvider: localStorage "cc-consent" + evento
// "cc-consent-updated".
const readConsentResolved = () => {
  try {
    return !!localStorage.getItem("cc-consent");
  } catch {
    // Sin storage el banner sigue renderizándose (no puede persistir la
    // decisión): NO abrir el chooser encima; el evento de consentimiento
    // lo desbloquea igualmente.
    return false;
  }
};

/** Rutas donde la ciudad condiciona el contenido. En legales, 404 o
 *  herramientas internas el modal solo estorbaría (p. ej. venir del banner de
 *  cookies a leer la política de privacidad). Las fichas ya traen la ciudad:
 *  /{ciudad}/pelicula/{slug} la fija desde la URL y la ficha de un cine adopta
 *  la del cine, así que un enlace directo (Google, redes) nunca abre el modal. */
const cityMatters = (pathname: string) =>
  pathname === "/" || pathname === "/peliculas" || pathname === "/cines";

/**
 * Selector de ciudad de primera visita (patrón RE:PÚBLICO). Obligatorio: no
 * mezclamos ciudades ni sesgamos a una por defecto, así que la primera visita
 * exige elección explícita (no se puede cerrar sin elegir). Se recuerda en
 * localStorage, así que solo ocurre una vez; después se cambia desde el
 * selector de la cabecera.
 */
export function CityChooserDialog() {
  const { t } = useTranslation();
  const { city, setCity } = useCity();
  const { pathname } = useLocation();
  const [consentResolved, setConsentResolved] = useState(readConsentResolved);

  useEffect(() => {
    if (consentResolved) return;
    // El evento significa "el visitante ya decidió" aunque el storage no
    // persista (p. ej. deshabilitado): resolvemos directamente.
    const onEvent = () => setConsentResolved(true);
    const poll = () => setConsentResolved((v) => v || readConsentResolved());
    window.addEventListener("cc-consent-updated", onEvent);
    // Respaldo por si el banner escribe sin emitir el evento.
    const interval = window.setInterval(poll, 1500);
    return () => {
      window.removeEventListener("cc-consent-updated", onEvent);
      window.clearInterval(interval);
    };
  }, [consentResolved]);

  const open = !city && consentResolved && cityMatters(pathname);

  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-rc-navy/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => {
            // Sin Trigger, radix devolvería el foco a <body>: lo mandamos al
            // contenido principal para que Tab siga desde ahí.
            e.preventDefault();
            const main = document.querySelector("main");
            if (main instanceof HTMLElement) {
              main.tabIndex = -1;
              main.focus({ preventScroll: true });
            }
          }}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 bg-rc-lavender p-6 text-rc-navy md:p-8 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          <Dialog.Title className="font-display text-4xl uppercase leading-[0.9] tracking-[-0.03em] text-rc-navy">
            {t("city.chooserTitle")}
          </Dialog.Title>
          <Dialog.Description className="mt-3 text-sm text-rc-navy">
            {t("city.chooserSubtitle")}
          </Dialog.Description>

          <div className="mt-6 flex flex-col gap-3">
            {SUPPORTED_CITIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCity(c, "chooser")}
                className="flex h-14 w-full items-center gap-3 rounded-full border-2 border-rc-navy bg-white px-5 font-display text-2xl uppercase text-rc-navy transition-[transform,background-color] duration-200 ease-spring hover:bg-rc-lime active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rc-violet"
              >
                <PinIcon className="h-5 w-5 shrink-0 text-rc-navy/70" />
                {c}
              </button>
            ))}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}
